import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ShieldAlert, Loader, Plus, X, Image as ImageIcon } from 'lucide-react';
import API_URL from '../../config';
import './Events.css';
import './EventsPrototype.css';

function formatDateRange(start, end) {
  if (!start) return '';
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const opts = { month: 'long', day: 'numeric', year: 'numeric' };
    if (e && s.getTime() !== e.getTime()) {
      return `${s.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}–${e.toLocaleDateString('en-US', opts)}`;
    }
    return s.toLocaleDateString('en-US', opts);
  } catch { return start; }
}

function getEventStatus(event) {
  if (event.status === 'live' || event.status === 'ongoing') return 'live';
  if (event.status === 'completed' || event.status === 'ended') return 'completed';
  const now = new Date();
  const start = event.start_date ? new Date(event.start_date) : null;
  const end = event.end_date ? new Date(event.end_date) : null;
  if (start && end && now >= start && now <= end) return 'live';
  if (end && now > end) return 'completed';
  return 'upcoming';
}

// Generate a monogram from event name
function getMonogram(name) {
  if (!name) return 'EV';
  const words = name.replace(/[^a-zA-Z0-9\s]/g, '').split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + '/' + words[1][0]).toUpperCase();
  return name.substring(0, 3).toUpperCase();
}

// Rotate cover art color per index
const COVER_COLORS = ['dark', 'lime', 'violet', ''];

export default function EventsHub() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Create event modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    slug: '',
    description: '',
    banner: '',
    start_date: '',
    end_date: '',
    venue: '',
    mode: 'In-Person',
    organization: 'VNRVJIET'
  });

  const fetchEvents = () => {
    fetch(`${API_URL}/events`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.events) {
          setEvents(data.events);
        }
      })
      .catch(err => console.error('Error fetching events:', err))
      .finally(() => setLoading(false));
  };

  // Enforce Light Theme only on Events Hub per user requirements
  useEffect(() => {
    const prevTheme = document.documentElement.getAttribute('data-theme') || localStorage.getItem('vjconnect_theme') || 'dark';
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      document.documentElement.setAttribute('data-theme', prevTheme);
    };
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('vjconnect_user'));
    if (storedUser) setUser(storedUser);

    fetchEvents();

    // Check admin status
    if (storedUser && storedUser.email) {
      fetch(`${API_URL}/events-admin/my-events?email=${storedUser.email}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.events && data.events.length > 0) {
            setIsAdmin(true);
          } else if (storedUser.role === 'admin') {
            setIsAdmin(true);
          }
        })
        .catch(() => {});
    }
  }, []);

  const handleImageFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewEvent(prev => ({ ...prev, banner: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleCreateEvent = async () => {
    if (!newEvent.name.trim() || !newEvent.slug.trim()) {
      alert('Event name and slug are required');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/events-admin/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newEvent,
          created_by: user?.email || 'admin@vnrvjiet.in',
          admin_email: user?.email || 'admin@vnrvjiet.in'
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(`Event "${newEvent.name}" created successfully!`);
        setShowCreateModal(false);
        setNewEvent({
          name: '',
          slug: '',
          description: '',
          banner: '',
          start_date: '',
          end_date: '',
          venue: '',
          mode: 'In-Person',
          organization: 'VNRVJIET'
        });
        fetchEvents();
      } else {
        alert(data.error || 'Failed to create event');
      }
    } catch (e) {
      console.error(e);
      alert('Error creating event');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="events-page events-hub-forced-light vp-font" data-theme="light">
      <div className="events-wrapper">
        {/* ====== TOP NAVIGATION LINK ====== */}
        <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/home" className="vp-btn vp-btn-soft vp-btn-small" style={{ textDecoration: 'none' }}>
            ← Back to VJLINK Portal
          </Link>
          {isAdmin && events.length > 0 && (
            <Link to={`/events/${events[0].slug}/admin`} className="vp-btn vp-btn-outline vp-btn-small" style={{ textDecoration: 'none' }}>
              <ShieldAlert size={14} /> Admin panel
            </Link>
          )}
        </div>

        {/* ====== HERO SECTION ====== */}
        <div className="vp-hero">
          <div>
            <div className="vp-eyebrow">Your campus. Your next connection.</div>
            <h1 style={{ fontSize: 'clamp(34px,5vw,58px)', lineHeight: 1.04, margin: '12px 0 16px', color: '#fff' }}>
              Good things happen when the right people meet.
            </h1>
            <p className="vp-lede" style={{ color: '#b7bbc5' }}>
              Discover campus events, meet people beyond your circle, and find the teammate who brings your next idea to life.
            </p>
            <div className="vp-hero-actions">
              {events.length > 0 && (
                <Link to={`/events/${events[0].slug}`} className="vp-btn vp-btn-lime" style={{ textDecoration: 'none' }}>
                  Explore {events[0].name} ↗
                </Link>
              )}
              {events.length > 0 && (
                <Link to={`/events/${events[0].slug}/networking`} className="vp-btn vp-btn-outline" style={{ textDecoration: 'none', borderColor: '#454954', color: '#fff' }}>
                  Meet people
                </Link>
              )}
            </div>
          </div>
          <div className="vp-hero-art" aria-hidden="true">
            <div className="vp-orbit">
              <div className="vp-orbit-center">VJ</div>
              <div className="vp-orbit-person vp-op1">AI</div>
              <div className="vp-orbit-person vp-op2">UX</div>
              <div className="vp-orbit-person vp-op3">DEV</div>
              <div className="vp-orbit-person vp-op4">+</div>
            </div>
          </div>
        </div>

        {/* ====== EVENTS SECTION HEADER ====== */}
        <div className="vp-section-head">
          <div>
            <div className="vp-eyebrow">What's happening</div>
            <h2 className="vp-heading" style={{ fontSize: 'clamp(27px,3.5vw,40px)', lineHeight: 1.12, margin: '9px 0 0' }}>
              Events worth showing up for
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {isAdmin && (
              <button onClick={() => setShowCreateModal(true)} className="vp-btn vp-btn-dark">
                <Plus size={16} /> Create event
              </button>
            )}
          </div>
        </div>

        {/* ====== EVENT CARDS ====== */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <Loader size={32} className="ev-spin" style={{ color: 'var(--vp-blue)' }} />
          </div>
        ) : events.length === 0 ? (
          <div className="vp-empty-state">
            <div className="vp-empty-icon">📅</div>
            <h3 className="vp-heading">No events yet</h3>
            <p>Events you can join and network at will appear here.</p>
            {isAdmin && (
              <button onClick={() => setShowCreateModal(true)} className="vp-btn vp-btn-dark">
                <Plus size={16} /> Create your first event
              </button>
            )}
          </div>
        ) : (
          <div className="vp-event-grid">
            {events.map((ev, idx) => {
              const status = getEventStatus(ev);
              const coverColor = COVER_COLORS[idx % COVER_COLORS.length];
              return (
                <Link
                  key={ev.slug}
                  to={`/events/${ev.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <article className="vp-event-card" tabIndex={0} aria-label={`Open ${ev.name}`}>
                    <div className={`vp-event-art ${coverColor}`} style={ev.banner ? {
                      backgroundImage: `url(${ev.banner})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    } : {}}>
                      <span className="vp-event-monogram">{getMonogram(ev.name)}</span>
                      <span className={`vp-pill ${status}`}>
                        {status === 'live' ? 'Happening now' : status === 'completed' ? 'Completed' : 'Upcoming'}
                      </span>
                    </div>
                    <div className="vp-event-card-body">
                      <div className="vp-eyebrow">{ev.organization || 'VNRVJIET'} · {ev.mode || 'Event'}</div>
                      <h3 className="vp-heading" style={{ fontSize: '20px', lineHeight: 1.2, marginBottom: '8px' }}>{ev.name}</h3>
                      {ev.description && (
                        <p style={{
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                        }}>
                          {ev.description}
                        </p>
                      )}
                      <div className="vp-event-meta">
                        <span>◷ {formatDateRange(ev.start_date, ev.end_date) || 'Date TBA'}</span>
                        {ev.venue && <span>⌖ {ev.venue}</span>}
                        <span>
                          <Users size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                          {ev.participant_count || 0} networking
                        </span>
                      </div>
                    </div>
                    <div className="vp-event-card-foot">
                      <span>Explore event & network</span>
                      <span>↗</span>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}

        {/* Admin panel link */}
        {isAdmin && events.length > 0 && (
          <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
            <Link to={`/events/${events[0].slug}/admin`} className="vp-btn vp-btn-outline" style={{ textDecoration: 'none' }}>
              <ShieldAlert size={16} /> Admin panel
            </Link>
          </div>
        )}
      </div>

      {/* ====== CREATE EVENT MODAL ====== */}
      {showCreateModal && (
        <div className="vp-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
          <div className="vp-modal" style={{ maxWidth: 540 }}>
            <div className="vp-modal-head">
              <div>
                <div className="vp-eyebrow">Event admin</div>
                <h2>Create an event.</h2>
              </div>
              <button className="vp-icon-btn" onClick={() => setShowCreateModal(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="vp-modal-body">
              <div className="vp-form-grid">
                <div className="vp-field full">
                  <label>Event Name *</label>
                  <input
                    placeholder="e.g. Convergence 2K26"
                    value={newEvent.name}
                    onChange={e => {
                      const val = e.target.value;
                      setNewEvent(prev => ({
                        ...prev,
                        name: val,
                        slug: prev.slug === '' || prev.slug === prev.name.toLowerCase().replace(/[^a-z0-9]/g, '')
                          ? val.toLowerCase().replace(/[^a-z0-9]/g, '') : prev.slug
                      }));
                    }}
                  />
                </div>
                <div className="vp-field">
                  <label>Event Slug *</label>
                  <input
                    placeholder="e.g. convergence2k26"
                    value={newEvent.slug}
                    onChange={e => setNewEvent({ ...newEvent, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  />
                  <span style={{ fontSize: '0.72rem', color: 'var(--vp-muted)' }}>URL: /events/{newEvent.slug || 'slug'}</span>
                </div>
                <div className="vp-field">
                  <label>Venue</label>
                  <input
                    placeholder="Campus / city"
                    value={newEvent.venue}
                    onChange={e => setNewEvent({ ...newEvent, venue: e.target.value })}
                  />
                </div>
                {/* Banner upload */}
                <div className="vp-field full">
                  <label>Cover Image / Banner</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      placeholder="Paste image URL (https://...)"
                      value={newEvent.banner.startsWith('data:') ? '' : newEvent.banner}
                      onChange={e => setNewEvent({ ...newEvent, banner: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <label className="vp-btn vp-btn-outline vp-btn-small" style={{ cursor: 'pointer' }}>
                      <ImageIcon size={14} /> Upload
                      <input type="file" accept="image/*" onChange={handleImageFile} style={{ display: 'none' }} />
                    </label>
                  </div>
                  {newEvent.banner && (
                    <div style={{ position: 'relative', height: 100, borderRadius: 14, overflow: 'hidden', marginTop: 8, border: `1px solid var(--vp-line)` }}>
                      <img src={newEvent.banner} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        onClick={() => setNewEvent({ ...newEvent, banner: '' })}
                        style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', color: '#fff', width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="vp-field full">
                  <label>Description</label>
                  <textarea
                    placeholder="What is this event about?"
                    rows={2}
                    value={newEvent.description}
                    onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                  />
                </div>
                <div className="vp-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={newEvent.start_date}
                    onChange={e => setNewEvent({ ...newEvent, start_date: e.target.value })}
                  />
                </div>
                <div className="vp-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={newEvent.end_date}
                    onChange={e => setNewEvent({ ...newEvent, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="vp-modal-foot">
              <button className="vp-btn vp-btn-outline" onClick={() => setShowCreateModal(false)} disabled={creating}>
                Cancel
              </button>
              <button className="vp-btn vp-btn-dark" onClick={handleCreateEvent} disabled={creating || !newEvent.name.trim() || !newEvent.slug.trim()}>
                {creating ? 'Creating...' : 'Create event →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
